import { useEffect, useMemo, useState } from "react";
import { useDogs } from "@/context/DogsContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Scissors, Dumbbell, Stethoscope, Tag, Printer } from "lucide-react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isToday,
} from "date-fns";
import { getDogColor, sortDogsForPalette } from "@/lib/dogColors";

const EVENT_ICON = {
  grooming: Scissors,
  training: Dumbbell,
  vet: Stethoscope,
  other: Tag,
};

export default function AllDogsMonthCalendar() {
  const { dogs } = useDogs();
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));
  const [eventsByDog, setEventsByDog] = useState({}); // { dogId: [events] }
  const [hiddenDogIds, setHiddenDogIds] = useState(() => new Set());
  const [loading, setLoading] = useState(false);

  const sortedDogIds = useMemo(
    () => sortDogsForPalette(dogs).map((d) => d.id),
    [dogs],
  );

  const monthLabel = format(monthAnchor, "MMMM yyyy");

  // Visible grid covers the full weeks that contain the month (Mon-start)
  const gridStart = useMemo(() => startOfWeek(startOfMonth(monthAnchor), { weekStartsOn: 1 }), [monthAnchor]);
  const gridEnd = useMemo(() => endOfWeek(endOfMonth(monthAnchor), { weekStartsOn: 1 }), [monthAnchor]);
  const days = useMemo(() => {
    const out = [];
    let cur = gridStart;
    while (cur <= gridEnd) {
      out.push(cur);
      cur = addDays(cur, 1);
    }
    return out;
  }, [gridStart, gridEnd]);

  // Load events for all dogs for this month (and adjacent month if grid spans it)
  useEffect(() => {
    if (!dogs || dogs.length === 0) {
      setEventsByDog({});
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const months = new Set();
        months.add(`${monthAnchor.getFullYear()}-${monthAnchor.getMonth() + 1}`);
        months.add(`${gridStart.getFullYear()}-${gridStart.getMonth() + 1}`);
        months.add(`${gridEnd.getFullYear()}-${gridEnd.getMonth() + 1}`);

        const calls = [];
        for (const dog of dogs) {
          for (const ym of months) {
            const [y, m] = ym.split("-").map((n) => parseInt(n, 10));
            calls.push(
              api
                .get("/events", { params: { dog_id: dog.id, year: y, month: m } })
                .then((r) => ({ dogId: dog.id, items: r.data.items || [] }))
                .catch(() => ({ dogId: dog.id, items: [] })),
            );
          }
        }
        const results = await Promise.all(calls);
        if (cancelled) return;
        const map = {};
        for (const r of results) {
          if (!map[r.dogId]) map[r.dogId] = [];
          // de-dupe by event id (same event may be returned across overlapping month queries)
          for (const it of r.items) {
            if (!map[r.dogId].find((e) => e.id === it.id)) map[r.dogId].push(it);
          }
        }
        setEventsByDog(map);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dogs, monthAnchor, gridStart, gridEnd]);

  const toggleDog = (dogId) => {
    setHiddenDogIds((prev) => {
      const next = new Set(prev);
      if (next.has(dogId)) next.delete(dogId);
      else next.add(dogId);
      return next;
    });
  };

  // Build a date → [{ event, dog, color }] map
  const eventsByDate = useMemo(() => {
    const out = {};
    for (const dog of dogs) {
      if (hiddenDogIds.has(dog.id)) continue;
      const color = getDogColor(dog.id, sortedDogIds);
      for (const ev of eventsByDog[dog.id] || []) {
        const ds = ev.event_date;
        if (!out[ds]) out[ds] = [];
        out[ds].push({ ev, dog, color });
      }
    }
    // Sort each day's events by type for stable display
    for (const ds of Object.keys(out)) {
      out[ds].sort((a, b) => (a.ev.event_type || "").localeCompare(b.ev.event_type || ""));
    }
    return out;
  }, [dogs, hiddenDogIds, eventsByDog, sortedDogIds]);

  if (dogs.length === 0) {
    return (
      <Card className="border-border shadow-none rounded-lg" data-testid="all-dogs-empty">
        <CardContent className="p-8 text-center text-muted-foreground text-sm">
          Add a dog to see the unified calendar.
        </CardContent>
      </Card>
    );
  }

  const weekHeaders = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div className="space-y-5" data-testid="all-dogs-month-calendar" data-print-root="month-calendar">
      {/* Month header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">All dogs · Month view</p>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tighter">{monthLabel}</h2>
        </div>
        <div className="flex items-center gap-2" data-print-hide>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMonthAnchor((m) => subMonths(m, 1))}
            data-testid="month-prev"
          >
            <ChevronLeft size={16} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMonthAnchor(startOfMonth(new Date()))}
            data-testid="month-this"
          >
            This month
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMonthAnchor((m) => addMonths(m, 1))}
            data-testid="month-next"
          >
            <ChevronRight size={16} />
          </Button>
          <Button
            size="sm"
            onClick={() => window.print()}
            data-testid="month-print"
            className="bg-primary text-primary-foreground hover:bg-primary/90 ml-1"
          >
            <Printer size={14} className="mr-1.5" /> Print / Save PDF
          </Button>
        </div>
      </div>

      {/* Legend / filter chips */}
      <div className="flex flex-wrap gap-2" data-testid="dog-legend">
        {sortedDogIds
          .map((id) => dogs.find((d) => d.id === id))
          .filter(Boolean)
          .map((dog) => {
            const color = getDogColor(dog.id, sortedDogIds);
            const hidden = hiddenDogIds.has(dog.id);
            return (
              <button
                key={dog.id}
                type="button"
                onClick={() => toggleDog(dog.id)}
                data-testid={`dog-chip-${dog.id}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                  hidden
                    ? "bg-card border-border text-muted-foreground line-through"
                    : "bg-card border-border text-foreground hover:-translate-y-0.5"
                }`}
                style={hidden ? undefined : { borderColor: color.solid }}
                aria-pressed={!hidden}
                title={hidden ? "Show this dog" : "Hide this dog"}
              >
                <span
                  className="h-2.5 w-2.5 rounded-full shrink-0"
                  style={{ background: hidden ? "transparent" : color.solid, border: `1px solid ${color.solid}` }}
                />
                {dog.name || "Unnamed"}
              </button>
            );
          })}
      </div>

      {/* Grid */}
      <Card className="border-border shadow-none rounded-lg overflow-hidden">
        <CardContent className="p-0">
          <div className="grid grid-cols-7 border-b border-border bg-secondary/50">
            {weekHeaders.map((h) => (
              <div key={h} className="text-[11px] uppercase tracking-wider text-muted-foreground px-3 py-2 text-center">
                {h}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((d) => {
              const ds = format(d, "yyyy-MM-dd");
              const inMonth = isSameMonth(d, monthAnchor);
              const dayEvents = eventsByDate[ds] || [];
              return (
                <div
                  key={ds}
                  data-testid={`month-day-${ds}`}
                  className={`border-r border-b border-border last:border-r-0 min-h-[110px] p-2 flex flex-col gap-1 ${
                    inMonth ? "bg-card" : "bg-muted/30"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-xs font-medium ${
                        isToday(d)
                          ? "h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center"
                          : inMonth
                          ? "text-foreground"
                          : "text-muted-foreground"
                      }`}
                    >
                      {format(d, "d")}
                    </span>
                    {dayEvents.length > 3 ? (
                      <span className="text-[10px] text-muted-foreground">+{dayEvents.length - 3}</span>
                    ) : null}
                  </div>
                  <div className="space-y-1 mt-0.5">
                    {dayEvents.slice(0, 3).map(({ ev, dog, color }) => {
                      const Icon = EVENT_ICON[ev.event_type] || Tag;
                      return (
                        <div
                          key={ev.id}
                          className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[11px] truncate border"
                          style={{
                            background: color.soft,
                            borderColor: color.solid,
                            color: color.solid,
                          }}
                          title={`${dog.name}: ${ev.title}`}
                          data-testid={`month-event-${ev.id}`}
                        >
                          <Icon size={10} className="shrink-0" />
                          <span className="truncate font-medium">
                            {dog.name}: {ev.title}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-xs text-muted-foreground text-center" data-testid="month-loading">Loading…</p>
      ) : null}
    </div>
  );
}
